import { t } from 'i18next';
import { PageLoader } from '../../common/components/PageLoader';
import { BaseController, BaseState } from '../../common/controllers/BaseController';
import { AuthenticationError } from '../../common/errors/errors';
import { AppConfigContext } from '../../common/services/AppConfigContext';
import { SuccessNotification } from '../../common/utils/notifications/Notification';
import { UrlHelper } from '../../common/utils/UrlHelper';
import { isAqueductEnabled, sanitizeNextUrl } from '../utils';

type Props = {};

type State = BaseState & {};

type ExpectedToken = {
  next: string | null;
  authToken: string | null;
  sessionTimeout: number | null;
  error: string | null;
};

export class SessionPickupController extends BaseController<Props, State> {
  private next: string | null;
  private sessionTimeout: number | null;

  // Store the JWT token when AQueduct is enabled
  private authToken: string | null;

  // Store any error message when AQueduct is enabled
  private error: string | null;

  constructor(props: Props, context: unknown) {
    super(props, context);

    try {
      const params = JSON.parse(atob(UrlHelper.getQueryParam('token', '') as string)) as ExpectedToken;
      const { next = null, authToken = null, sessionTimeout = null, error = null } = params;
      this.next = next;
      this.authToken = authToken;
      this.sessionTimeout = Number(sessionTimeout);
      this.error = error;

      // Clear all search parameters
      if (process.env.NODE_ENV === 'production') {
        UrlHelper.clearQueryParams();
      }
    } catch {
      this.next = null;
      this.authToken = null;
      this.sessionTimeout = null;
    }
  }

  componentDidMount() {
    super.componentDidMount();

    try {
      const useAqueduct = isAqueductEnabled();
      // Only AQueduct returns an error to `SessionPickupController`
      const effectiveError = useAqueduct ? this.error : null;

      if (!this.next || !this.sessionTimeout) {
        throw new AuthenticationError(effectiveError ?? undefined);
      }

      if (useAqueduct) {
        // In the case of AQueduct, we have an `authToken`.
        if (!this.authToken) {
          throw new AuthenticationError(effectiveError ?? undefined);
        }

        AppConfigContext.setToken(this.authToken, this.sessionTimeout);
      }
      this.showMessage(new SuccessNotification(t('loggedIn')));

      const next = sanitizeNextUrl(this.next);
      this.redirect(next);
    } catch (e: unknown) {
      this.handleError(e, { authError: t('unsuccessfulSSOLogin') });
    }
  }

  render() {
    return this.renderBase(<PageLoader />);
  }
}
